import { type FC, useCallback, useEffect } from 'react'

import type { VaultMediaItemDto } from '@api'
import { useUnit } from 'effector-react'
import _ from 'lodash/fp'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { DialogProps } from '@/dialogs/types'
import {
  $finishedPullingSentMedia,
  $mediaErrorMessage,
  $sentMedia,
} from '@/models/chattingUI/media.ts'
import { getUserMessageEditorModels } from '@/models/chattingUI/messageEditor/messageEditor.ts'
import { $isMediaCloningEnabled } from '@/models/features.ts'
import { $hasDeleteMediaPermission } from '@/models/permissions.ts'
import { toast } from '@/models/toasts.ts'
import {
  $selectedMediaIds,
  $vaultMediasMap,
  resetSelectedMedia,
} from '@/models/vault/medias'
import {
  $isCategoryMediaOpen,
  closeVault,
  openVault,
} from '@/models/vault/vault.ts'
import { VaultCategories } from '@/modules/vault/ui/category/vault-categories.tsx'
import { VaultCategorySelect } from '@/modules/vault/ui/category/vault-category-select.tsx'
import { VaultMedia } from '@/modules/vault/ui/media/vault-media.tsx'
import { classNames } from '@/utils/helpers.ts'

interface Props extends DialogProps {
  userId: number
}

export const SelectMediaFromVaultDialog: FC<Props> = ({
  isOpen,
  onClose,
  userId,
}) => {
  const sentMedia = useUnit($sentMedia)
  const finishedPullingSentMedia = useUnit($finishedPullingSentMedia)
  const mediaErrorMessage = useUnit($mediaErrorMessage)
  const selectedIds = useUnit($selectedMediaIds)
  const {
    medias: {
      events: { addVaultMedias },
    },
    cloneMedia: {
      effects: { cloneMediaFx },
    },
    tags: {
      events: { addTags },
    },
  } = getUserMessageEditorModels(userId)
  const vaultMediasMap = useUnit($vaultMediasMap)
  const isCategoryMediaOpen = useUnit($isCategoryMediaOpen)

  const isMediaCloningEnabled = useUnit($isMediaCloningEnabled)
  const hasDeleteMediaPermission = useUnit($hasDeleteMediaPermission)
  const handleClose = useCallback(() => {
    closeVault()
    resetSelectedMedia()
    onClose()
  }, [onClose])

  const handleAdd = useCallback(() => {
    const media = selectedIds.map((id) => vaultMediasMap[id]!).filter(Boolean)

    const tagsFromMedias = _.uniqBy(
      'id',
      media.flatMap(({ releaseForms }) => releaseForms),
    )
    const totalPrice = media.reduce(
      (sum, { mediaAttributes }) => sum + (mediaAttributes?.price ?? 0),
      0,
    )

    // Hotfix to disable price set on vault media select.
    // setPrice((old) => (old ? old + totalPrice : (old || 0) + totalPrice))
    addTags(tagsFromMedias)
    addVaultMedias(media)
    handleClose()
  }, [addTags, addVaultMedias, handleClose, selectedIds, vaultMediasMap])

  const handleClone = useCallback(
    async (mediaItem: VaultMediaItemDto) => {
      toast.success('Starting to clone media...')

      await cloneMediaFx(mediaItem.id)
      handleClose()
    },
    [handleClose, cloneMediaFx],
  )

  useEffect(() => {
    if (isOpen) {
      openVault()
    }
  }, [isOpen])

  return (
    <Dialog
      panelClassName="mt-0 sm:mt-0 max-h-[calc(100vh-10vh)] h-full flex flex-col"
      onClose={handleClose}
      wideXxl
      bigDialog
      show={isOpen}
      childrenClassName="flex-1 overflow-hidden flex flex-col"
    >
      <Dialog.Header>
        <Dialog.Title>Vault</Dialog.Title>
      </Dialog.Header>

      <div className="lg-pl-0 mt-6 flex rounded-2xl border p-4 flex-1 overflow-hidden box-border">
        <div
          className={classNames('lg:block', { hidden: isCategoryMediaOpen })}
        >
          <VaultCategories
            isInSelect
            showTitle={false}
            searchInSort
          />
        </div>
        <div
          className={classNames(
            'w-full flex lg:!flex flex-col overflow-hidden',
            {
              hidden: !isCategoryMediaOpen,
            },
          )}
        >
          <div>
            <div className="mb-3 flex items-center lg:hidden">
              <VaultCategorySelect />
            </div>
          </div>

          <div className="overflow-hidden flex flex-1">
            <VaultMedia
              showTypeSelection
              isForSelection
              showAddMedia={false}
              showSettings
              hasDeleteMediaPermission={hasDeleteMediaPermission}
              onClone={isMediaCloningEnabled ? handleClone : undefined}
            />
          </div>
        </div>
      </div>

      {(!finishedPullingSentMedia || !mediaErrorMessage) && (
        <div className="mt-4 w-full text-center">
          {!!mediaErrorMessage && (
            <div className="text-danger-default text-xs font-medium">
              Error! Couldn&apos;t load sent media. Try to reload the page.{' '}
              {mediaErrorMessage}
            </div>
          )}

          {!finishedPullingSentMedia && (
            <div className="text-primary-800 dark:text-secondary-100 text-sm font-medium">
              <span className="text-danger-default">Warning!</span> Still
              loading sent media for this fan... (already loaded{' '}
              {sentMedia.length})
            </div>
          )}
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button onClick={handleAdd}>Add media</Button>
      </div>
    </Dialog>
  )
}

SelectMediaFromVaultDialog.displayName = 'SelectMediaFromVaultDialog'
